import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { API_URL } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import './VetoFlow.css';

// Pool category names are technical/proper nouns — kept as-is across languages.
const CATEGORIES = {
  active: 'Active Pool',
  reserve: 'Reserve',
  classic: 'Classic',
  community: 'Community',
};

// Sort-mode labels are translated; keys resolved via t() at render time
// (module-level constant can't call hooks).
const SORT_MODES = {
  default: 'veto.sortDefault',
  newest: 'veto.sortNewest',
  least_played: 'veto.sortLeastPlayed',
};

/**
 * Map pool selector with category grouping and veto settings.
 * Game mode is received as a prop from the game_mode step.
 *
 * Fetches `/api/web/maps` for the available pool and `/api/maps/stats` for
 * per-map play counts, then allows sorting within each category by either
 * creation date (newest added first) or play count (least played first).
 * Unplayed maps get `times_played: 0` and bubble to the top of the
 * "least played" view, which is the whole point of that sort mode.
 *
 * @param {Object} props
 * @param {Object} props.match - Match object with team data
 * @param {string} [props.gameMode] - Game mode filter (competitive/wingman/duel)
 * @param {Function} props.onVetoStarted - Callback when veto is started
 * @param {boolean} [props.demoMode=false] - If true, skip the `/api/veto/.../start`
 *   POST and hand the selected `{pool, knife_round, veto_mode, match_type}`
 *   straight to `onVetoStarted`. Lets the veto demo reuse this component
 *   without a real match on the backend.
 */
export default function MapPoolSelector({ match, gameMode: gameModeProp, onVetoStarted, demoMode = false }) {
  const { t } = useTranslation();
  const { getAuthHeaders } = useAuth();
  const [maps, setMaps] = useState([]);
  const [selectedTags, setSelectedTags] = useState(new Set());
  const [sortMode, setSortMode] = useState('default');
  // Use prop if provided, otherwise infer from player count (fallback for tournament/admin usage)
  const playerCount = (match?.team1?.players?.length || 0) + (match?.team2?.players?.length || 0);
  const gameMode = gameModeProp || (playerCount <= 2 ? 'duel' : playerCount <= 4 ? 'wingman' : 'competitive');

  const [knifeRound, setKnifeRound] = useState(gameMode !== 'duel');
  const [vetoMode, setVetoMode] = useState('team_vote');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Pre-seed the pool from a match's configured maplist exactly once (e.g. a
  // league launch copies the league's curated pool onto match.maplist). After
  // that, the user's toggles own the selection.
  const seededRef = useRef(false);

  useEffect(() => {
    const params = gameMode && gameMode !== 'all' ? `?game_mode=${gameMode}` : '';
    Promise.all([
      fetch(`${API_URL}/api/web/maps${params}`).then(r => r.json()).catch(() => []),
      fetch(`${API_URL}/api/maps/stats`).then(r => r.json()).catch(() => ({ map_stats: [] })),
    ]).then(([mapsData, statsData]) => {
      const playedByTag = {};
      for (const s of (statsData?.map_stats || [])) {
        playedByTag[s.map_tag] = s.times_played || 0;
      }
      const merged = (mapsData || []).map(m => ({
        ...m,
        times_played: playedByTag[m.tag] || 0,
      }));
      setMaps(merged);
    });
  }, [gameMode]);

  // Seed the selection from match.maplist once the maps for this mode are
  // loaded, keeping only tags that actually exist in the pool. The backend
  // defaults maplist to ALL maps at create time (queue + wizard flows), so a
  // preset covering the whole pool is not a curated selection — only a strict
  // subset (e.g. a league's map pool) should pre-seed; otherwise start empty.
  useEffect(() => {
    if (seededRef.current || !maps.length) return;
    const preset = Array.isArray(match?.maplist) ? match.maplist : [];
    if (preset.length === 0) return;
    const available = new Set(maps.map((m) => m.tag));
    const seed = preset.filter((t) => available.has(t));
    if (seed.length > 0 && seed.length < maps.length) setSelectedTags(new Set(seed));
    seededRef.current = true;
  }, [maps, match]);

  const toggleMap = (tag) => {
    setSelectedTags(prev => {
      const next = new Set(prev);
      if (next.has(tag)) next.delete(tag);
      else next.add(tag);
      return next;
    });
  };

  const selectCategory = (category) => {
    const catTags = maps.filter(m => m.category === category).map(m => m.tag);
    setSelectedTags(prev => {
      const next = new Set(prev);
      catTags.forEach(t => next.add(t));
      return next;
    });
  };

  const clearCategory = (category) => {
    const catTags = new Set(maps.filter(m => m.category === category).map(m => m.tag));
    setSelectedTags(prev => {
      const next = new Set(prev);
      catTags.forEach(t => next.delete(t));
      return next;
    });
  };

  const handleStart = async () => {
    const pool = [...selectedTags];
    const minNeeded = { BO1: 1, BO3: 3, BO5: 5 }[match.type] || 1;
    if (pool.length < minNeeded) {
      setError(minNeeded === 1
        ? t('veto.minMapsOne', { count: minNeeded, type: match.type })
        : t('veto.minMapsMany', { count: minNeeded, type: match.type }));
      return;
    }

    if (demoMode) {
      onVetoStarted({
        pool,
        knife_round: knifeRound,
        veto_mode: vetoMode,
        match_type: match.type || 'BO1',
      });
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_URL}/api/veto/${match.id}/start`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pool,
          knife_round: knifeRound,
          veto_mode: vetoMode,
          match_type: match.type || 'BO1',
        }),
      });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail?.error || err.detail || t('veto.startFailedDefault'));
      }
      const state = await response.json();
      onVetoStarted(state);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Group maps by category, then sort within each group according to sortMode.
  // "default" keeps server order (category + order + name); "newest" puts recently
  // added maps first; "least_played" surfaces unplayed/rarely-played maps.
  const grouped = {};
  for (const m of maps) {
    const cat = m.category || 'active';
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(m);
  }
  if (sortMode === 'newest') {
    for (const cat of Object.keys(grouped)) {
      grouped[cat] = [...grouped[cat]].sort((a, b) => {
        const aT = a.created_at ? Date.parse(a.created_at) : 0;
        const bT = b.created_at ? Date.parse(b.created_at) : 0;
        return bT - aT;
      });
    }
  } else if (sortMode === 'least_played') {
    for (const cat of Object.keys(grouped)) {
      grouped[cat] = [...grouped[cat]].sort((a, b) => {
        const diff = (a.times_played || 0) - (b.times_played || 0);
        if (diff !== 0) return diff;
        return (a.name || a.tag).localeCompare(b.name || b.tag);
      });
    }
  }

  return (
    <div className="map-pool-selector">
      <h2>{t('veto.poolTitle')}</h2>
      <p className="veto-desc">{t('veto.poolDesc')}</p>

      {error && <div className="veto-error">{error}</div>}

      {/* Settings row */}
      <div className="pool-settings">
        <div className="setting-group">
          <label>{t('veto.vetoModeLabel')}</label>
          <div className="toggle-group">
            <button
              className={`toggle-btn ${vetoMode === 'team_vote' ? 'active' : ''}`}
              onClick={() => setVetoMode('team_vote')}
            >
              {t('veto.modeTeamVote')}
            </button>
            <button
              className={`toggle-btn ${vetoMode === 'captain' ? 'active' : ''}`}
              onClick={() => setVetoMode('captain')}
            >
              {t('veto.poolCaptain')}
            </button>
          </div>
        </div>
        <div className="setting-group">
          <label>{t('veto.knifeLabel')}</label>
          <button
            className={`toggle-btn ${knifeRound ? 'active' : ''}`}
            onClick={() => setKnifeRound(!knifeRound)}
          >
            {knifeRound ? t('veto.on') : t('veto.off')}
          </button>
        </div>
        <div className="setting-group">
          <label>{t('veto.sortLabel')}</label>
          <div className="toggle-group">
            {Object.entries(SORT_MODES).map(([key, labelKey]) => (
              <button
                key={key}
                className={`toggle-btn ${sortMode === key ? 'active' : ''}`}
                onClick={() => setSortMode(key)}
              >
                {t(labelKey)}
              </button>
            ))}
          </div>
        </div>
        <div className="setting-group">
          <span className="pool-count">{selectedTags.size === 1
            ? t('veto.mapsSelectedOne', { count: selectedTags.size })
            : t('veto.mapsSelectedMany', { count: selectedTags.size })}</span>
        </div>
      </div>

      {/* Map categories */}
      {Object.entries(CATEGORIES).map(([cat, label]) => {
        const catMaps = grouped[cat] || [];
        if (catMaps.length === 0) return null;
        const allSelected = catMaps.every(m => selectedTags.has(m.tag));

        return (
          <div key={cat} className="pool-category">
            <div className="category-header">
              <h3>{label}</h3>
              <button
                className="category-toggle"
                onClick={() => allSelected ? clearCategory(cat) : selectCategory(cat)}
              >
                {allSelected ? t('veto.deselectAll') : t('veto.selectAll')}
              </button>
            </div>
            <div className="pool-map-grid">
              {catMaps.map(m => {
                const imgExt = m.tag === 'de_train' ? 'png' : 'jpg';
                return (
                <div
                  key={m.tag}
                  className={`pool-map-card ${selectedTags.has(m.tag) ? 'selected' : ''}`}
                  onClick={() => toggleMap(m.tag)}
                  style={{ backgroundImage: `url(/stats/images/maps/${m.tag}.${imgExt})` }}
                >
                  <span className="pool-map-name">{m.name || m.tag}</span>
                  {sortMode === 'least_played' && (
                    <span className="pool-map-plays">
                      {(m.times_played || 0) === 0 ? t('veto.unplayed') : t('veto.playedTimes', { count: m.times_played })}
                    </span>
                  )}
                  <span className="pool-map-check">
                    {selectedTags.has(m.tag) ? '\u2713' : ''}
                  </span>
                </div>
                );
              })}
            </div>
          </div>
        );
      })}

      <button
        className="veto-start-btn"
        onClick={handleStart}
        disabled={loading || selectedTags.size < ({ BO1: 1, BO3: 3, BO5: 5 }[match.type] || 1)}
      >
        {loading
          ? t('veto.starting')
          : selectedTags.size === ({ BO1: 1, BO3: 3, BO5: 5 }[match.type] || 1)
            ? (selectedTags.size === 1 ? t('veto.noVetoOne') : t('veto.noVetoMany'))
            : t('veto.startVeto', { count: selectedTags.size })}
      </button>
    </div>
  );
}
